import { useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { MailCheckIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { authErrorMessage } from "../../shared/auth";
import {
  requestPasswordReset,
  type PasswordResetFailure,
  type PasswordResetRequestOutcome,
} from "../../shared/auth/password-reset";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

/** The message for a failed reset request or reset; shared with `ResetPasswordPage`. */
export function passwordResetFailureMessage(
  failure: PasswordResetFailure,
  t: (key: string) => string,
): string {
  switch (failure.status) {
    case "not-configured":
      return t("auth/passwordRecoveryByEmail_pcqczl");
    case "unavailable":
      return t("auth/thePasswordRecoveryService_f85hsh");
    case "rate-limited":
      return t("auth/tooManyRequestsWait_b64kzc");
    case "rejected":
      return authErrorMessage(failure.error);
  }
}

/**
 * Asks platform-api to mail a password-reset link. The answer is the same
 * whether or not the address has an account, so the page says only that a
 * letter went out if there was one to send.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");

  const mutation = useMutation<PasswordResetRequestOutcome, never, string>({
    mutationFn: requestPasswordReset,
  });
  const outcome = mutation.data;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutation.mutate(email.trim());
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("auth/passwordRecovery_i2bznj")}</CardTitle>
          {outcome?.status === "sent" ? null : (
            <CardDescription>{t("auth/enterTheEmailAddress_i1poks")}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {outcome?.status === "sent" ? (
            <Alert variant="success">
              <MailCheckIcon />
              <AlertDescription>
                <p>{t("auth/ifAnAccountWith_s01we7")}</p>
                <p>{t("auth/ifTheEmailHas_3e6o74")}</p>
              </AlertDescription>
            </Alert>
          ) : (
            <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="forgot-password-email">{t("auth/emailAddress_1m22un")}</Label>
                <Input
                  id="forgot-password-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              {outcome !== undefined ? (
                <p className="text-destructive text-sm" role="alert">
                  {passwordResetFailureMessage(outcome, t)}
                </p>
              ) : null}
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? t("auth/sending_vj9pt4") : t("auth/sendLink_7tiw7f")}
              </Button>
            </form>
          )}
          <p className="text-muted-foreground text-sm">
            <Link className="text-primary underline-offset-4" to="/sign-in">
              {t("auth/backToSignIn_x0t0pf")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
